import { useEffect, useState } from 'react'
import { Divider } from 'components/divider'
import { useTranslation } from 'react-i18next'
import { EyeIcon, EllipsisVerticalIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import i18next, { t } from 'i18next'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/table'
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu } from 'components/dropdown'
import { useNavigate, useLocation } from 'react-router-dom'
import { Input, InputGroup } from 'components/input'
import { useLazyQuery } from '@apollo/client'
import loadingImage from 'assets/images/boat-loading.gif'
import { GET_ROUTE_PRICE_DISCOUNT } from 'graphql/common/queries'
import { customDateFormat } from 'utils/common'
import { Pagination, PaginationList, PaginationNext, PaginationPage, PaginationPrevious } from 'components/pagination'
import { Badge } from 'components'
import { getPageRange } from 'utils/paginationHelper'

export function ConfigDiscounts() {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { state } = useLocation()
    const [configDiscountList, setConfigDiscountList] = useState([])
    const [listCount, setListCount] = useState(0)
    const [searchString, setSearchString] = useState('') // search string

    // pagination
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const totalPages = Math.ceil(listCount / rowsPerPage)

    const [getConfigDiscountList, { loading: discountListLoading, error: discountListListError }] = useLazyQuery(
        GET_ROUTE_PRICE_DISCOUNT,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.getRoutePriceDiscounts.success && data.getRoutePriceDiscounts.code === '200') {
                    setConfigDiscountList(data.getRoutePriceDiscounts.route_price_discounts || [])
                    setListCount(data.getRoutePriceDiscounts.total_count)
                }
            },
        }
    )
    const getSystemDiscountConfig = searchStr => {
        getConfigDiscountList({
            variables: {
                filter: {
                    discount_code: searchStr || null,
                    status_code: null,
                    valid_from: null,
                    valid_to: null,
                },
                page: {
                    limit: rowsPerPage,
                    offset: page * rowsPerPage,
                },
            },
        })
    }
    useEffect(() => {
        getSystemDiscountConfig(searchString)
    }, [page, rowsPerPage])
    const handlePageChange = newPage => {
        setPage(newPage)
    }
    const onClickSearch = () => {
        console.log('searchString', searchString)
        setPage(0)
        setRowsPerPage(10)
        getSystemDiscountConfig(searchString)
    }
    const onClickSearchClear = () => {
        console.log('searchString before clear:', searchString)
        setSearchString('') // Clear the search string
        setPage(0)
        setRowsPerPage(10)
        getSystemDiscountConfig()
    }
    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <div className="mt-8 mb-4 flex flex-col sm:flex-row  max-w-xl gap-4">
                        <div className="flex-1">
                            <InputGroup>
                                <MagnifyingGlassIcon />
                                <Input
                                    id="search"
                                    placeholder={t('Search discount code')}
                                    aria-label="Search discount Code"
                                    value={searchString}
                                    onChange={e => setSearchString(e.target.value)}
                                    onKeyPress={e => {
                                        if (e.key === 'Enter') {
                                            onClickSearch()
                                        }
                                    }}
                                />
                            </InputGroup>
                        </div>
                        <div className="flex gap-2 justify-end">
                            <button
                                type="button"
                                onClick={onClickSearch}
                                className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                            >
                                {t('Search')}
                            </button>
                            <button
                                type="button"
                                onClick={onClickSearchClear}
                                className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                                {t('Clear')}
                            </button>
                        </div>
                    </div>
                </div>
                <div>
                    <div className="mt-4 mb-4 flex flex-col sm:flex-row  max-w-xl gap-4">
                        <button
                            onClick={() => navigate(`/system-config/discount`, { state: { currentTab: 'Discounts' } })}
                            type="button"
                            className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                        >
                            {t('Add Discount')}
                        </button>
                    </div>
                </div>
            </div>
            {discountListLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">{t('Loading discounts')}...</p>
                </div>
            )}
            {discountListListError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch records!')}</div>
                </div>
            )}
            {!discountListLoading && !discountListListError && configDiscountList?.length === 0 && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No data found!')}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t('Try adjusting your search criteria')}</p>
                    <div className="mt-6"></div>
                </div>
            )}
            {!discountListLoading && !discountListListError && configDiscountList?.length > 0 && (
                <>
                    <div className="table-custom overflow-x-scroll lg:overflow-hidden rounded-b-lg bg-white p-0 flex mt-2">
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{t('Discount Code')}</TableHeader>
                                    <TableHeader>{t('Description')}</TableHeader>
                                    <TableHeader>{t('Route Type')}</TableHeader>
                                    <TableHeader>{t('Country')}</TableHeader>
                                    <TableHeader>{t('Validity')}</TableHeader>
                                    <TableHeader>{t('status')}</TableHeader>
                                    <TableHeader>{t('Action')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {configDiscountList.map(discount => (
                                    <TableRow
                                        className="cursor-pointer hover:bg-gray-50"
                                        onClick={() =>
                                            navigate(`/system-config/discount/`, {
                                                state: { discountInfo: discount, currentTab: 'Discounts' },
                                            })
                                        }
                                        key={discount.discount_id}
                                    >
                                        <TableCell>{discount.discount_code}</TableCell>
                                        <TableCell>{discount.discount_desc.en}</TableCell>
                                        <TableCell>
                                            {discount.discount_conditions?.route_details?.description[i18next?.language || 'en'] || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {discount.discount_conditions?.country_details?.country || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {customDateFormat(discount.valid_from)} -{' '}
                                            {customDateFormat(discount.valid_to)}
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                color={discount.status.description.en == 'Active' ? 'emerald' : 'rose'}
                                            >
                                                {discount.status.description[i18next?.language || 'en']}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Dropdown>
                                                <DropdownButton
                                                    plain
                                                    aria-label="More options"
                                                    className="cursor-pointer"
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom">
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(`/system-config/discount/`, {
                                                                state: {
                                                                    discountInfo: discount,
                                                                    currentTab: 'Discounts',
                                                                },
                                                            })
                                                        }}
                                                    >
                                                        <EyeIcon />
                                                        <DropdownLabel>{t('Edit')}</DropdownLabel>
                                                    </DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <Divider />
                    <Pagination className="py-6">
                        <PaginationPrevious
                            onClick={() => handlePageChange(page > 0 ? page - 1 : 0)}
                            disabled={page === 0}
                        />
                        <PaginationList>
                            {getPageRange(page, totalPages).map(index => (
                                <PaginationPage
                                    key={index}
                                    current={index === page}
                                    onClick={() => handlePageChange(index)}
                                >
                                    {index + 1}
                                </PaginationPage>
                            ))}
                        </PaginationList>
                        <PaginationNext
                            onClick={() => handlePageChange(page < totalPages - 1 ? page + 1 : totalPages - 1)}
                            disabled={page === totalPages - 1}
                        />
                    </Pagination>
                    <Divider />
                </>
            )}
        </>
    )
}
